import {
  DayHeaderContentArg,
  EventApi,
  EventContentArg,
  MoreLinkArg,
} from '@fullcalendar/core';
import deLocale from '@fullcalendar/core/locales/de';
import enGbLocale from '@fullcalendar/core/locales/en-gb';
import esLocale from '@fullcalendar/core/locales/es';
import nlLocale from '@fullcalendar/core/locales/nl';
import DayGridPlugin from '@fullcalendar/daygrid';
import InteractionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import { Locales } from '@griply/types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CalendarSyncStates,
  FullCalendarEvent,
  ToggleCalendarVisibility,
} from 'shared/components/ui/full-calendar-event';
import { FullCalendarMorePopover } from 'shared/components/ui/full-calendar-more-popover';
import { weekdaysNumberMap } from 'shared/constants';
import { useForwardedRef } from 'shared/hooks/use-forwarded-ref';
import { useResizeObserver } from 'shared/hooks/use-resize-observer';
import { eventAllow } from 'shared/lib/full-calendar/event-allow';
import { eventOrder } from 'shared/lib/full-calendar/event-order';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { SmartList } from 'shared/types/smart-list';
import { Tag } from 'shared/types/tag';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';

import { DateHeader } from './date-header';
import * as Styled from './full-calendar-month.style';

const plugins = [DayGridPlugin, InteractionPlugin];
const supportedLocales = [nlLocale, enGbLocale, esLocale, deLocale];
const localeMap: Record<Locales, string> = {
  [Locales.NlNL]: 'nl',
  [Locales.EnUS]: 'en-gb',
  [Locales.EsES]: 'es',
  [Locales.DeDE]: 'de',
};

export type FullCalendarMonthProps = Omit<
  React.ComponentProps<typeof FullCalendar>,
  | 'plugins'
  | 'initialView'
  | 'dayHeaderContent'
  | 'eventAllow'
  | 'height'
  | 'locales'
  | 'locale'
  | 'dayMaxEvents'
  | 'eventContent'
> & {
  tasks: Task[];
  goals: Goal[];
  lifeAreas: LifeArea[];
  tags: Tag[];
  smartLists: SmartList[];
  weekStartsOn: WeekDays;
  onCompleteTask: (id: ID, date: Timestamp) => void;
  onClickEvent?: (id: ID) => void;
  referenceDate?: Timestamp;
  fullCalendarRef?: React.RefObject<FullCalendar | null>;
  calendarSyncStates?: CalendarSyncStates;
  onToggleCalendarVisibility?: ToggleCalendarVisibility;
};

export const FullCalendarMonth: React.FC<FullCalendarMonthProps> = ({
  tasks,
  goals,
  lifeAreas,
  tags,
  smartLists,
  weekStartsOn,
  onCompleteTask,
  onClickEvent,
  referenceDate = new Date(),
  fullCalendarRef,
  events,
  calendarSyncStates,
  onToggleCalendarVisibility,
  ...rest
}) => {
  const locale = useI18nLocale();
  const ref = useForwardedRef(fullCalendarRef);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [moreInfo, setMoreInfo] = useState<{
    position: { top: number; left: number };
    events: EventApi[];
  }>();

  const onResize = useCallback(() => {
    ref.current?.getApi().updateSize();
    ref.current
      ?.getApi()
      .setOption(
        'eventMinWidth',
        (scrollContainerRef.current?.clientWidth ?? 7) / 7,
      );
  }, [ref]);
  useResizeObserver(scrollContainerRef, onResize, 1);

  const onMore = useCallback(({ jsEvent, hiddenSegs }: MoreLinkArg) => {
    jsEvent.preventDefault();
    setMoreInfo({
      position: {
        top: (jsEvent as MouseEvent).clientY,
        left: (jsEvent as MouseEvent).clientX,
      },
      events: hiddenSegs.map(({ event }) => event),
    });

    return '';
  }, []);
  const closeMore = useCallback(() => setMoreInfo(undefined), []);

  const _onClickEvent = useCallback(
    (id: ID) => {
      onClickEvent?.(id);
      closeMore();
    },
    [closeMore, onClickEvent],
  );

  const renderEvent = useCallback(
    ({ event, isPast }: EventContentArg) => (
      <FullCalendarEvent
        event={event}
        isPast={isPast}
        tasks={tasks}
        goals={goals}
        lifeAreas={lifeAreas}
        tags={tags}
        smartLists={smartLists}
        onComplete={onCompleteTask}
        onClick={_onClickEvent}
        calendarSyncStates={calendarSyncStates}
        onToggleCalendarVisibility={onToggleCalendarVisibility}
      />
    ),
    [
      _onClickEvent,
      goals,
      lifeAreas,
      tags,
      smartLists,
      onCompleteTask,
      tasks,
      calendarSyncStates,
      onToggleCalendarVisibility,
    ],
  );

  const renderDayHeader = useCallback(
    (arg: DayHeaderContentArg) => <DateHeader {...arg} />,
    [],
  );

  // Update the calendar view when referenceDate changes
  useEffect(() => {
    if (referenceDate && ref.current) {
      queueMicrotask(() => ref.current?.getApi().gotoDate(referenceDate));
    }
  }, [referenceDate, ref]);

  useEffect(() => {
    // if events have updated, close the more list
    closeMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when events are updated we close the more
  }, [events]);

  return (
    <Styled.ScrollContainer ref={scrollContainerRef}>
      <Styled.Container>
        <FullCalendar
          ref={ref}
          plugins={plugins}
          initialView="dayGridMonth"
          fixedWeekCount={false}
          dayHeaderContent={renderDayHeader}
          eventAllow={eventAllow}
          height="100%"
          locales={supportedLocales}
          locale={localeMap[locale]}
          dayMaxEvents={4}
          moreLinkClick={onMore}
          eventContent={renderEvent}
          eventOrder={eventOrder}
          firstDay={weekdaysNumberMap[weekStartsOn]}
          events={events}
          {...rest}
        />

        {moreInfo && (
          <FullCalendarMorePopover
            events={moreInfo.events}
            location={moreInfo.position}
            onOutside={closeMore}
            weekStartsOn={weekStartsOn}
            tasks={tasks}
            goals={goals}
            lifeAreas={lifeAreas}
            onComplete={onCompleteTask}
            tags={tags}
            smartLists={smartLists}
            onClick={_onClickEvent}
            calendarSyncStates={calendarSyncStates}
            onToggleCalendarVisibility={onToggleCalendarVisibility}
          />
        )}
      </Styled.Container>
    </Styled.ScrollContainer>
  );
};
